import React, { useState } from 'react';
import { 
  DollarSign, 
  Search, 
  Filter,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  User,
  Package,
  CreditCard,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Mock data for COD collection
const mockCODRecords = [
  {
    id: 'COD_001',
    customerId: 'customer_001',
    customerName: 'Nguyễn Văn A',
    customerPhone: '0901234567',
    totalCOD: 1200000,
    totalOrders: 8,
    weekPeriod: '02/2025',
    dateRange: '06/01/2025 - 12/01/2025',
    status: 'paid',
    paidDate: '2025-01-13T10:00:00+07:00',
    paidAmount: 1200000,
    bankAccount: 'VCB 1234567890',
    transferReference: 'TF20250113001',
    receipt: 'receipt_001.pdf',
    orders: ['DH001', 'DH002', 'DH003', 'DH004', 'DH005', 'DH006', 'DH007', 'DH008']
  },
  {
    id: 'COD_002',
    customerId: 'customer_002',
    customerName: 'Trần Thị B',
    customerPhone: '0987654321',
    totalCOD: 850000,
    totalOrders: 5,
    weekPeriod: '02/2025',
    dateRange: '06/01/2025 - 12/01/2025',
    status: 'pending',
    orders: ['DH009', 'DH010', 'DH011', 'DH012', 'DH013']
  },
  {
    id: 'COD_003',
    customerId: 'customer_003',
    customerName: 'Lê Văn C',
    customerPhone: '0369852147',
    totalCOD: 2100000,
    totalOrders: 12,
    weekPeriod: '01/2025',
    dateRange: '30/12/2024 - 05/01/2025',
    status: 'paid',
    paidDate: '2025-01-06T15:30:00+07:00',
    paidAmount: 2100000,
    bankAccount: 'TCB 9876543210',
    transferReference: 'TF20250106002',
    receipt: 'receipt_002.pdf',
    orders: ['DH014', 'DH015', 'DH016', 'DH017', 'DH018', 'DH019', 'DH020', 'DH021', 'DH022', 'DH023', 'DH024', 'DH025']
  }
];

const CODCollection = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  const filteredRecords = mockCODRecords.filter(record => {
    const matchesSearch = record.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.customerPhone.includes(searchTerm) ||
                         record.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || record.status === filterStatus;
    const matchesTab = activeTab === 'all' || record.status === activeTab;
    return matchesSearch && matchesStatus && matchesTab;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const markAsPaid = (recordId) => {
    // Mock function to mark COD as paid
    alert(`Đánh dấu COD ${recordId} đã thanh toán`);
  };

  const downloadReceipt = (record) => {
    // Mock function to download receipt
    alert(`Tải xuống hóa đơn: ${record.receipt}`);
  };

  const totalStats = {
    pending: mockCODRecords.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.totalCOD, 0),
    paid: mockCODRecords.filter(r => r.status === 'paid').reduce((sum, r) => sum + r.totalCOD, 0),
    total: mockCODRecords.reduce((sum, r) => sum + r.totalCOD, 0)
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Quản lý thu COD</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Chờ thanh toán</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {totalStats.pending.toLocaleString()}đ
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Đã thanh toán</p>
                <p className="text-2xl font-bold text-green-600">
                  {totalStats.paid.toLocaleString()}đ
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tổng COD</p>
                <p className="text-2xl font-bold text-primary">
                  {totalStats.total.toLocaleString()}đ
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Chờ thanh toán
          </TabsTrigger>
          <TabsTrigger value="paid" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Đã thanh toán
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Tất cả
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Danh sách COD */}
            <Card className="lg:col-span-2 animate-fade-in">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-primary" />
                    Danh sách COD
                  </CardTitle>
                  <div className="flex space-x-2">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                      <Input
                        placeholder="Tìm kiếm..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-48"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredRecords.map((record) => (
                    <div
                      key={record.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                        selectedRecord?.id === record.id ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                      onClick={() => setSelectedRecord(record)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="bg-primary/10 p-2 rounded-lg">
                            {getStatusIcon(record.status)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{record.customerName}</h3>
                              <Badge variant="outline" className="text-xs">{record.id}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Tuần {record.weekPeriod} ({record.dateRange})
                            </p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(record.status)}>
                          {record.status === 'paid' ? 'Đã thanh toán' : 'Chờ thanh toán'}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span>{record.customerPhone}</span>
                          </div>
                          <div className="flex items-center">
                            <Package className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span>{record.totalOrders} đơn hàng</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="text-right">
                            <span className="font-medium text-primary text-lg">
                              {record.totalCOD.toLocaleString()}đ
                            </span>
                          </div>
                          {record.status === 'paid' && record.paidDate && (
                            <div className="text-right text-xs text-muted-foreground">
                              Thanh toán: {new Date(record.paidDate).toLocaleDateString('vi-VN')}
                            </div>
                          )}
                        </div>
                      </div>

                      {record.status === 'pending' && (
                        <div className="flex justify-end mt-3 pt-3 border-t">
                          <Button 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsPaid(record.id);
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            Đánh dấu đã thanh toán
                          </Button>
                        </div>
                      )}

                      {record.status === 'paid' && record.receipt && (
                        <div className="flex justify-end mt-3 pt-3 border-t">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadReceipt(record);
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Tải hóa đơn
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Chi tiết COD */}
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle>Chi tiết COD</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedRecord ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h3 className="font-semibold mb-2">Thông tin khách hàng</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Tên:</span> {selectedRecord.customerName}</p>
                        <p><span className="font-medium">SĐT:</span> {selectedRecord.customerPhone}</p>
                        <p><span className="font-medium">ID:</span> {selectedRecord.customerId}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h3 className="font-semibold mb-2">Thông tin COD</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Kỳ đối soát:</span> Tuần {selectedRecord.weekPeriod}</p>
                        <p><span className="font-medium">Thời gian:</span> {selectedRecord.dateRange}</p>
                        <p><span className="font-medium">Tổng đơn:</span> {selectedRecord.totalOrders} đơn</p>
                        <p><span className="font-medium">Tổng COD:</span> 
                          <span className="text-primary font-bold ml-1">
                            {selectedRecord.totalCOD.toLocaleString()}đ
                          </span>
                        </p>
                      </div>
                    </div>

                    {selectedRecord.status === 'paid' && (
                      <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <h3 className="font-semibold mb-2 text-green-800 dark:text-green-200">Thông tin thanh toán</h3>
                        <div className="space-y-2 text-sm">
                          <p><span className="font-medium">Ngày TT:</span> {new Date(selectedRecord.paidDate).toLocaleString('vi-VN')}</p>
                          <p><span className="font-medium">Số tiền:</span> {selectedRecord.paidAmount.toLocaleString()}đ</p>
                          <p><span className="font-medium">Tài khoản:</span> {selectedRecord.bankAccount}</p>
                          <p><span className="font-medium">Mã GD:</span> {selectedRecord.transferReference}</p>
                        </div>
                      </div>
                    )}

                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h3 className="font-semibold mb-2">Danh sách đơn hàng</h3>
                      <div className="max-h-40 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {selectedRecord.orders.map(orderId => (
                            <Badge key={orderId} variant="outline" className="justify-center">
                              {orderId}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p>Chọn một bản ghi COD để xem chi tiết</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CODCollection;